<script>
import {saveAs} from 'file-saver';
import AddInvoiceModal from "@/components/modals/AddInvoiceModal.vue";
import FiltersPanel from "@/components/filters/FiltersPanel.vue";
import PerPageSelect from "@/components/ui/PerPageSelect.vue";

export default {
    components: {FiltersPanel, AddInvoiceModal, PerPageSelect},
    computed: {
        filtersDef() {
            return [
                {key: 'invoice_number', label: this.$t('invoices.fields.invoice_number.name'), type: 'text'},
                {key: 'issue_date', label: this.$t('invoices.fields.issue_date.name'), type: 'date'},
                {key: 'net_amount', label: this.$t('invoices.fields.net_amount.name'), type: 'number'},
                {key: 'gross_amount', label: this.$t('invoices.fields.gross_amount.name'), type: 'number'},
                {
                    key: 'type', label:  this.$t('invoices.fields.type.name'), type: 'select',
                    options: [
                        {label: this.$t('invoices.types.cost'), value: 'cost'},
                        {label: this.$t('invoices.types.income'), value: 'income'},
                    ]
                },
                ...(this.$auth.isAdmin() ? [{
                    key: 'company_id',
                    label: this.$t('users.fields.company.name'),
                    type: 'select',
                    options: this.companies ? this.companies.map(company => ({
                        label: company.name,
                        value: company.id
                    })) : []
                }] : []),
            ]
        }

    },
    data() {
        return {
            loading: true,
            downloading: false,
            data: [],
            meta: {
                current_page: 1,
                total: 0,
                per_page: 0,
            },
            sort: {
                field: 'id',
                order: 'desc'
            },
            addInvoiceModalOpened: false,
            controller: {
                data: null,
                companies: null
            },
            selectedInvoice: null,
            companyId: null,
            companies: [],
            filters: {},
            perPage: parseInt(localStorage.getItem('perPage_invoices') || '10'),
        }
    },
    methods: {
        onFiltersChange(filters) {
            this.filters = filters;
            this.meta.current_page = 1;
            this.loadData();
        },
        loadData() {
            this.controller.data = new AbortController();
            this.loading = true;
            window.scrollTo({top: 0});
            let params = {
                page: this.meta.current_page,
                per_page: this.perPage,
                sort: this.sort.field,
                order: this.sort.order,
                filters: this.filters
            };
            if (this.companyId) {
                params.company_id = this.companyId;
            }
            axios.get('/invoices', {
                signal: this.controller.data.signal,
                params: params
            }).then(response => {
                this.data = response.data.data;
                this.meta.current_page = response.data.meta.current_page;
                this.meta.total = response.data.meta.total;
                this.meta.per_page = response.data.meta.per_page;
            }).catch(error => {
            }).finally(() => {
                this.loading = false;
            })
        },
        downloadXlsx() {
            this.downloading = true;
            let params = {
                sort: this.sort.field,
                order: this.sort.order,
                filters: this.filters,
                xlsx: 1
            }
            axios({
                url: '/invoices',
                method: 'GET',
                responseType: 'blob',
                params
            }).then((response) => {
                saveAs(response.data, 'invoices.xlsx');
            }).finally(() => {
                this.downloading = false;
            });
        },
        onPageChange(page) {
            this.controller.data.abort();
            this.meta.current_page = page;
            this.loadData();
        },
        onSort(field, order) {
            this.controller.data.abort();
            this.sort = {field, order};
            this.loadData();
        },
        onPerPageChange() {
            localStorage.setItem('perPage_invoices', String(this.perPage));
            this.meta.current_page = 1;
            this.loadData();
        },
        editInvoice(invoice) {
            this.selectedInvoice = invoice;
            this.openAddInvoiceModal();
        },
        openAddInvoiceModal() {
            this.addInvoiceModalOpened = true;
        },
        closeAddInvoiceModal(data) {
            this.selectedInvoice = null;
            if (data && data.saved) {
                this.loadData();
            }
            this.addInvoiceModalOpened = false;
        },
        download(invoice) {
            invoice.downloading = true;
            axios({
                url: '/invoices/' + invoice.id + '/download',
                method: 'GET',
                responseType: 'blob',
            }).then((response) => {
                saveAs(response.data, invoice.file_name);
            }).finally(() => {
                invoice.downloading = false;
            });
        },
        loadCompanies() {
            if (this.controller.companies) {
                this.controller.companies.abort();
            }
            this.controller.companies = new AbortController();
            this.loadingCompanies = true;
            axios.get('/companies',{
                signal: this.controller.companies.signal
            }).then(response => {
                this.companies = response.data.data;
            }).catch(error => {
            }).finally(() => {
                this.loadingCompanies = false;
            })
        }
    },
    mounted() {
        this.loadData();
        if (this.$auth.isAdmin()) {
            this.loadCompanies();
        }
    },
    beforeUnmount() {
        this.controller.data && this.controller.data.abort();
        this.controller.companies && this.controller.companies.abort();
    }
}

</script>
<template>
    <section>

        <add-invoice-modal :invoice="selectedInvoice"
                           v-if="addInvoiceModalOpened"
                           @close="closeAddInvoiceModal"/>

        <div class="mb-4">
            <FiltersPanel
                :filters="filtersDef"
                @update:activeFilters="onFiltersChange"
            >
                <template #buttons>
                    <b-button type="is-link"
                              v-if="$auth.isAdmin()"
                              @click="openAddInvoiceModal">
                        {{ $t('invoices.labels.add_invoice') }}
                    </b-button>
                    <b-button v-if="$auth.isAdmin()" :loading="downloading" type="is-link" icon-left="download" @click="downloadXlsx">
                        .xlsx
                    </b-button>
                </template>
            </FiltersPanel>
        </div>

        <b-table
            :data="loading ? [] : data"
            :loading="loading"

            :paginated="!loading"
            backend-pagination
            :total="loading ? 0 : meta.total"
            :per-page="perPage"
            @page-change="onPageChange"
            :aria-next-label="$t('navigation.next_page')"
            :aria-previous-label="$t('navigation.previous_page')"
            :aria-page-label="$t('navigation.page')"
            :aria-current-label="$t('navigation.current_page')"

            backend-sorting
            sort-icon="arrow-up"
            default-sort-direction="asc"
            :default-sort="['issue_date', 'desc']"
            @sort="onSort">

            <template #empty>
                <div v-if="!loading" class="block has-text-grey has-text-centered mb-4 mt-4">
                    {{ $t('invoices.labels.no_invoices') }}
                </div>
            </template>

            <b-table-column field="invoice_number" :label="$t('invoices.fields.number.name')" v-slot="props">
                {{ props.row.invoice_number }}
            </b-table-column>

            <b-table-column field="company.name" :label="$t('companies.fields.name.name')" sortable v-slot="props"
                            v-if="$auth.isAdmin()">
                {{ props.row.company.name }}
            </b-table-column>

            <b-table-column field="issue_date" :label="$t('invoices.fields.issue_date.name')" sortable v-slot="props">
                {{ props.row.issue_date }}
            </b-table-column>

            <b-table-column field="net_amount" :label="$t('invoices.fields.net_amount.name')" v-slot="props">
                {{ props.row.net_amount_formatted }}
            </b-table-column>

            <b-table-column field="gross_amount" :label="$t('invoices.fields.gross_amount.name')" v-slot="props">
                {{ props.row.gross_amount_formatted }}
            </b-table-column>

            <b-table-column field="type" :label="$t('invoices.fields.type.name')" v-slot="props">
                {{ $t('invoices.types.' + props.row.type) }}
            </b-table-column>


            <b-table-column field="" label="-" v-slot="props">

                <section class="buttons">

                    <b-button type="is-link" size="is-small"
                              :class="{ 'is-loading': props.row.downloading }"
                              :disabled="props.row.downloading"
                              :title="$t('actions.download')"
                              icon-right="file-download-outline"
                              @click="download(props.row)">
                    </b-button>

                    <b-button type="is-primary" size="is-small"
                              v-if="$auth.isAdmin()"
                              :class="{ 'is-loading': props.row.downloading }"
                              :disabled="props.row.downloading"
                              :title="$t('actions.edit')"
                              icon-right="square-edit-outline"
                              @click="editInvoice(props.row)">
                    </b-button>

                </section>

            </b-table-column>

            <template #bottom-left>
                <PerPageSelect v-model="perPage" @change="onPerPageChange" />
            </template>

        </b-table>

        <loader :loading="loading"></loader>
    </section>
</template>
